import './styles/global.css';
import { useState, useEffect, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { MainLayout } from './components/layout';
import { PrintBarcodeProvider } from './components/barcode';
import { db } from './services/firebase';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CategoryProvider, useCategory } from './context';
import { InventoryProvider } from './context/InventoryContext';
import { ReportsProvider } from './context/ReportsContext'; // Import ReportsProvider
import { PageLoader } from './components/ui';
import { LazyRoute } from './components/common';
import { preloadRepairData } from './services/repairService';
import { checkForAppUpdate, showUpdateNotification } from './utils/versionUtils';

// Lazy load page components
const LoginPage = lazy(() => import('./pages/LoginPage'));
const InventoryPage = lazy(() => import('./pages/InventoryPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const RepairManagement = lazy(() => import('./pages/RepairManagement'));
const TransactionsPage = lazy(() => import('./pages/TransactionsPage'));
const ReportsPage = lazy(() => import('./pages/ReportsPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage')); // Dashboard import

// Protected route component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <PageLoader fullScreen message="Yükleniyor..." />;
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
};

// Data initialization component
const DataInitializer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { fetchCategories, categories } = useCategory();
  const [isInitializing, setIsInitializing] = useState(false);
  const [repairsPreloaded, setRepairsPreloaded] = useState(false);
  
  // Handle categories initialization
  useEffect(() => {
    const initializeData = async () => {
      if (user && !isInitializing) {
        try {
          setIsInitializing(true);
          // We're checking if categories need updating - pass forceRefresh: false
          // to rely on the persistent initialization state
          if (categories.length === 0) {
            await fetchCategories(false);
          }
        } catch (error) {
          console.error('Failed to initialize data:', error);
        } finally {
          setIsInitializing(false);
        }
      }
    };
    
    initializeData();
  }, [user, fetchCategories, categories.length, isInitializing]);
  
  // Preload repair data for faster loading
  useEffect(() => {
    const preloadRepairs = async () => {
      if (user && !repairsPreloaded) {
        try {
          await preloadRepairData();
          setRepairsPreloaded(true);
        } catch (error) {
          console.error('Failed to preload repair data:', error);
          // Still mark as preloaded to avoid retrying repeatedly
          setRepairsPreloaded(true);
        }
      }
    };
    
    preloadRepairs();
  }, [user, repairsPreloaded]);
  
  return <>{children}</>;
};

// App main content component
const AppContent: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [firestoreError, setFirestoreError] = useState<string | null>(null);
  
  useEffect(() => {
    // Initialize Firestore connection
    const initializeFirestore = async () => {
      try {
        if (!db) {
          throw new Error('Firestore yüklenemedi');
        }
        setIsLoading(false);
      } catch (error: any) {
        console.error('Veri yüklenirken hata oluştu:', error);
        setFirestoreError('Veri yüklenemedi. Lütfen sayfayı yenileyin.');
        setIsLoading(false);
      }
    };

    initializeFirestore();
  }, []);
  
  // Show loading state
  if (isLoading) {
    return <PageLoader fullScreen message="Veri yükleniyor..." />;
  }
  
  // Show error state if there was a problem loading data
  if (firestoreError) {
    return (
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="text-center max-w-md">
          <div className="text-red-500 mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-primary-charcoal">Veri Yükleme Hatası</h3>
          <p className="text-primary-charcoal/70 mt-2">{firestoreError}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-primary-charcoal text-accent-white rounded-md hover:bg-primary-charcoal/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-charcoal"
          >
            Sayfayı Yenile
          </button>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={
        <LazyRoute message="Giriş sayfası yükleniyor...">
          <LoginPage />
        </LazyRoute>
      } />
      <Route element={
        <ProtectedRoute>
          <DataInitializer>
            <MainLayout />
          </DataInitializer>
        </ProtectedRoute>
      }>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={
          <LazyRoute message="Gösterge paneli yükleniyor...">
            <DashboardPage />
          </LazyRoute>
        } />
        <Route path="/inventory" element={
          <LazyRoute>
            <InventoryPage />
          </LazyRoute>
        } />
        <Route path="/profile" element={
          <LazyRoute>
            <ProfilePage />
          </LazyRoute>
        } />
        <Route path="/repairs" element={
          <LazyRoute>
            <RepairManagement />
          </LazyRoute>
        } />
        <Route path="/transactions" element={
          <LazyRoute>
            <TransactionsPage />
          </LazyRoute>
        } />
        <Route path="/reports" element={
          <LazyRoute>
            <ReportsPage />
          </LazyRoute>
        } />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Route>
    </Routes>
  );
};

function App() {
  useEffect(() => {
    // Check for app updates periodically
    const checkUpdates = async () => {
      const hasUpdate = await checkForAppUpdate();
      if (hasUpdate) {
        showUpdateNotification();
      }
    };

    // Check immediately
    checkUpdates();

    // Check every 30 minutes for updates
    const updateInterval = setInterval(checkUpdates, 30 * 60 * 1000);

    // Check when user returns to the app (visibility change)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkUpdates();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(updateInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return (
    <AuthProvider>
      <InventoryProvider>
        <CategoryProvider>
          <ReportsProvider> {/* Wrap with ReportsProvider */}
            <PrintBarcodeProvider>
              <AppContent />
            </PrintBarcodeProvider>
          </ReportsProvider>
        </CategoryProvider>
      </InventoryProvider>
    </AuthProvider>
  );
}

export default App;